// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/Document/ask/citations';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = { 'Access-Control-Allow-Origin': '*' };

// ============= Types =============

export interface GetCitationDocumentProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  askSessionId: string;
  askMessageId: string;
  documentId: string;
}

// ============= Utility Functions =============
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

export interface GetCitationDocumentResult {
  url: string;
  blob: Blob;
}

// ============= Main Function =============
/**
 * Fetches a cited document for an ask session message.
 * Used to load the document when the user opens a citation.
 *
 * @param getAccessToken Function to acquire access token
 * @param askSessionId Ask session identifier
 * @param askMessageId Ask message identifier (the message that contains the citation)
 * @param documentId Document (source) identifier from the citation
 * @returns Promise with blob URL and blob (for caching)
 */
export const getCitationDocument = async ({
  getAccessToken,
  askSessionId,
  askMessageId,
  documentId,
}: GetCitationDocumentProps): Promise<GetCitationDocumentResult> => {
  try {
    console.log('🔄 Fetching citation document...');

    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    const url = `${BASE_URL}${API_ENDPOINT}/${askSessionId}/message/${askMessageId}/document/${documentId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token),
    });

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const streamUrl = URL.createObjectURL(blob);
    console.log('✅ Citation document fetched successfully');

    return { url: streamUrl, blob };
  } catch (error) {
    console.error('🔴 Error fetching citation document:', error);
    throw error;
  }
};
